import BigNumber from 'big.js';
import { API } from '@xbto/api-client';
import {
  subHours,
  subWeeks,
  subYears,
  subMonths,
  getUnixTime,
  isDate,
} from 'date-fns';
import {
  AssetFundChartTimeRange,
  AssetFundTradingViewChartData,
  ChartTimeRange,
  TradingViewCandleStickChartData,
  TradingViewLineChartData,
  AssetFundTradingViewMarkerChartData,
  AssetFundTradingViewLineChartData,
} from '../types';
import { formatters } from './formatters';
import { getFallbackMantissa } from './format-amount';

export const getFundChartingOptions = (
  asset: string,
  performanceComputationMethod: API.PerformanceComputationMethod,
  timeRange: AssetFundChartTimeRange,
  inceptionDate?: Date
): API.GetAssetBalanceHistoryRequest => {
  const now = new Date();

  if (inceptionDate) {
    return {
      startDate: inceptionDate,
      endDate: now,
      periodType: API.PeriodType.Monthly,
      asset,
      performanceComputationMethod,
    };
  }

  if (timeRange === AssetFundChartTimeRange.Y1) {
    return {
      startDate: subYears(now, 1),
      endDate: now,
      periodType: API.PeriodType.Monthly,
      asset,
      performanceComputationMethod,
    };
  }

  return {
    startDate: null,
    endDate: null,
    periodType: API.PeriodType.Monthly,
    asset,
    performanceComputationMethod,
  };
};

export const getChartingOptions = (
  timeRange?: ChartTimeRange | undefined,
  type: 'balance' | 'asset' = 'asset',
  ccyCode?: string
): API.OhlcvsRequest | API.GetAccountBalanceHistoryRequest => {
  const now = new Date();

  if (timeRange === ChartTimeRange.Y3) {
    if (type === 'balance') {
      return {
        startDate: subYears(now, 3),
        endDate: now,
        resolution: API.GetAccountBalanceHistoryResolution.Week1,
        quoteAsset: null,
        baseAsset: null,
      };
    }
    return {
      base: ccyCode,
      quote: 'USD',
      startDate: subYears(now, 3),
      endDate: now,
      resolution: API.CandleResolutions.Days1,
    } as API.OhlcvsRequest;
  }

  if (timeRange === ChartTimeRange.Y1) {
    if (type === 'balance') {
      return {
        startDate: subYears(now, 1),
        endDate: now,
        resolution: API.GetAccountBalanceHistoryResolution.Week1,
        quoteAsset: null,
        baseAsset: null,
      };
    }
    return {
      base: ccyCode,
      quote: 'USD',
      startDate: subYears(now, 1),
      endDate: now,
      resolution: API.CandleResolutions.Days1,
    } as API.OhlcvsRequest;
  }

  if (timeRange === ChartTimeRange.M3) {
    if (type === 'balance') {
      return {
        startDate: subMonths(now, 3),
        endDate: now,
        resolution: API.GetAccountBalanceHistoryResolution.Week1,
        quoteAsset: null,
        baseAsset: null,
      };
    }
    return {
      base: ccyCode,
      quote: 'USD',
      startDate: subMonths(now, 3),
      endDate: now,
      resolution: API.CandleResolutions.Days1,
    } as API.OhlcvsRequest;
  }

  if (timeRange === ChartTimeRange.M1) {
    if (type === 'balance') {
      return {
        startDate: subMonths(now, 1),
        endDate: now,
        resolution: API.GetAccountBalanceHistoryResolution.Day1,
        quoteAsset: null,
        baseAsset: null,
      };
    }
    return {
      base: ccyCode,
      quote: 'USD',
      startDate: subMonths(now, 1),
      endDate: now,
      resolution: API.CandleResolutions.Days1,
    } as API.OhlcvsRequest;
  }

  if (timeRange === ChartTimeRange.W1) {
    if (type === 'balance') {
      return {
        startDate: subWeeks(now, 1),
        endDate: now,
        resolution: API.GetAccountBalanceHistoryResolution.Hour1,
        quoteAsset: null,
        baseAsset: null,
      };
    }
    return {
      base: ccyCode,
      quote: 'USD',
      startDate: subWeeks(now, 1),
      endDate: now,
      resolution: API.CandleResolutions.Days1,
    } as API.OhlcvsRequest;
  }

  if (timeRange === ChartTimeRange.H24) {
    if (type === 'balance') {
      return {
        startDate: subHours(now, 24),
        endDate: now,
        resolution: API.GetAccountBalanceHistoryResolution.Minute5,
        quoteAsset: null,
        baseAsset: null,
      };
    }
    return {
      base: ccyCode,
      quote: 'USD',
      startDate: subHours(now, 24),
      endDate: now,
      resolution: API.CandleResolutions.Hours1,
    } as API.OhlcvsRequest;
  }

  if (type === 'balance') {
    return {
      startDate: subHours(now, 1),
      endDate: now,
      resolution: API.GetAccountBalanceHistoryResolution.Minute1,
      quoteAsset: null,
      baseAsset: null,
    };
  }
  return {
    base: ccyCode,
    quote: 'USD',
    startDate: subHours(now, 1),
    endDate: now,
    resolution: API.CandleResolutions.Minutes5,
  } as API.OhlcvsRequest;
};

export const getOhlcvChartingData = (
  items: API.Ohlcv[] | null,
  fxRate: BigNumber | undefined,
  displayAsGrowth?: boolean
) => {
  if (!items || !items.length) {
    return null;
  }

  const areaChartDataPoints: TradingViewLineChartData[] = [];
  const candleStickChartDataPoints: TradingViewCandleStickChartData[] = [];
  let decimalPrecision = 0;

  const arrLength = items.length - 1;
  for (const [index, item] of items.entries()) {
    if (!item || !item.timestamp) {
      continue;
    }
    // timestamp is TZ string from talos - convert to unix timestamp for lightweight chart to understand
    const date = new Date(item.timestamp);
    const unixTime = getUnixTime(date);
    let precision = 0;

    const isLastItem = index === arrLength;
    // This is to match the last data point's fx price to be the same as our fx rate
    // but we want sometimes (for Funds) we want to ignore the data point coming from fx rate
    if (isLastItem && fxRate && !displayAsGrowth) {
      precision = getFallbackMantissa(fxRate.toFixed());
      // Acceptable loss of precision, even for synthetic pairs. See SHIB/BTC on TV
      const value = fxRate.toNumber();
      const formattedValue = formatters.getBaseCurrency(fxRate, {
        decimals: precision,
      });

      areaChartDataPoints.push({
        time: unixTime,
        value,
        formatted: { value: formattedValue },
      });
      candleStickChartDataPoints.push({
        time: unixTime,
        open: Number(item.open),
        high: Number(item.high),
        low: Number(item.low),
        close: value,
        formatted: { value: formattedValue },
      });
    } else {
      precision = getFallbackMantissa(item.close);
      const value = Number(item.close);
      const formattedValue = displayAsGrowth
        ? formatters.getAmount(item.close, 2)
        : formatters.getBaseCurrency(item.close, { decimals: precision });

      areaChartDataPoints.push({
        time: unixTime,
        value,
        formatted: { value: formattedValue },
      });
      candleStickChartDataPoints.push({
        time: unixTime,
        open: Number(item.open),
        high: Number(item.high),
        low: Number(item.low),
        close: Number(item.close),
        formatted: { value: formattedValue },
      });
    }
    decimalPrecision =
      precision > decimalPrecision ? precision : decimalPrecision;
  }

  return {
    areaChartDataPoints,
    candleStickChartDataPoints,
    decimalPrecision,
  };
};

export const getBalanceHistoryChartingData = (
  result: API.GetAccountBalanceHistoryResponse | null,
  latestBalance: string | null | undefined
) => {
  if (!result) {
    return null;
  }

  const items = result.datapoints;
  if (!items) {
    return null;
  }

  const areaChartDataPoints: TradingViewLineChartData[] = [];
  const arrLength = items.length - 1;
  let decimalPrecision = 0;

  for (const [index, item] of items.entries()) {
    if (!item || !item.date) {
      continue;
    }
    // timestamp is TZ string from talos - convert to unix timestamp for lightweight chart to understand
    const date = new Date(item.date);
    const unixTime = getUnixTime(date);
    let precision = 0;

    const isLastItem = index === arrLength;
    if (isLastItem && latestBalance) {
      precision = getFallbackMantissa(latestBalance);
      const value = Number(latestBalance);
      areaChartDataPoints.push({
        time: unixTime,
        value,
        formatted: {
          value: formatters.getBaseCurrency(latestBalance, {
            decimals: precision,
          }),
        },
      });
    } else {
      precision = getFallbackMantissa(item.value);
      const value = item.value;
      areaChartDataPoints.push({
        time: unixTime,
        value,
        formatted: {
          value: formatters.getBaseCurrency(value.toString(), {
            decimals: precision,
          }),
        },
      });
    }
    decimalPrecision =
      precision > decimalPrecision ? precision : decimalPrecision;
  }

  return {
    areaChartDataPoints,
    decimalPrecision,
  };
};

const _convertMarkersToDataPoints = (
  data: API.GraphMarker[] | null,
  resultInKind: API.GetAssetBalanceHistoryResponse[] | null,
  resultUsd: API.GetAssetBalanceHistoryResponse[] | null
) => {
  if (!data) {
    return null;
  }

  const usdValueMarkers: AssetFundTradingViewMarkerChartData[] = [];
  const assetAmountMarkers: AssetFundTradingViewMarkerChartData[] = [];

  for (const [, item] of data.entries()) {
    if (!item || !item.date || !item.asset || !item.secondaryAssetCode) {
      continue;
    }

    const date = new Date(item.date);
    const unixTime = getUnixTime(date);

    const matchedAssetAmountItem = resultInKind?.find(r => {
      if (isDate(r.periodStart) && isDate(item.date)) {
        return r.periodStart.toUTCString() === item.date.toUTCString();
      }
      return r.periodStart === item.date;
    });
    if (matchedAssetAmountItem) {
      assetAmountMarkers.push({
        time: unixTime,
        value: Number(matchedAssetAmountItem.balance),
      });
    }

    const matchedUsdValueItem = resultUsd?.find(r => {
      if (isDate(r.periodStart) && isDate(item.date)) {
        return r.periodStart.toUTCString() === item.date.toUTCString();
      }
      return r.periodStart === item.date;
    });
    if (matchedUsdValueItem) {
      usdValueMarkers.push({
        time: unixTime,
        value: Number(matchedUsdValueItem.balance),
      });
    }
  }

  return { usdValueMarkers, assetAmountMarkers };
};

const _getTimeAndValue = (item: API.GetAssetBalanceHistoryResponse) => {
  const time = getUnixTime(new Date(item.periodStart));
  const value = Number(item.balance);
  return {
    time,
    value,
  };
};

const _getMatchingMarker = (
  markers: API.GraphMarker[] | null,
  item: API.GetAssetBalanceHistoryResponse
) => {
  if (!markers) {
    return undefined;
  }

  const result = markers.find(r => {
    if (isDate(r.date) && isDate(item.periodStart)) {
      return r.date.toUTCString() === item.periodStart.toUTCString();
    }
    return r.date === item.periodStart;
  });

  return result || undefined;
};

const _convertBalanceHistoryToDataPoints = (
  _data: API.GetAssetBalanceHistoryResponse[] | null,
  markers: API.GraphMarker[] | null
) => {
  if (!_data) {
    return null;
  }
  const result: AssetFundTradingViewLineChartData[] = [];

  const _dataLength = _data.length;

  const _actualData: API.GetAssetBalanceHistoryResponse[] = [];
  const _fakedData: API.GetAssetBalanceHistoryResponse[] = [];

  for (let i = 0; i < _dataLength; i++) {
    if (i % 2 === 0) {
      _actualData.push(_data[i]);
    } else {
      _fakedData.push(_data[i]);
    }
  }

  for (const [index, item] of _actualData.entries()) {
    if (!item || !item.periodStart) {
      continue;
    }

    const marker = _getMatchingMarker(markers, item);
    const fakeItem = _fakedData[index];

    const itemData = _getTimeAndValue(item);
    const fakeItemData = _getTimeAndValue(fakeItem);

    let steps: number[] | undefined;
    if (fakeItemData && fakeItemData.value !== itemData.value) {
      steps = [itemData.value, fakeItemData.value];
    }

    result.push({
      time: itemData.time,
      value: itemData.value,
      marker,
      steps,
    });
  }

  return result;
};

export const getAssetBalanceHistoryChartingData = (
  asset: string,
  resultInKind: API.GetAssetBalanceHistoryResponse[] | null,
  resultUsd: API.GetAssetBalanceHistoryResponse[] | null,
  markers: API.GraphMarker[] | null,
  timeRange: AssetFundChartTimeRange
): AssetFundTradingViewChartData => {
  const _markers = _convertMarkersToDataPoints(
    markers,
    resultInKind,
    resultUsd
  );

  return {
    asset,
    timeRange,
    assetAmountDataPoints: _convertBalanceHistoryToDataPoints(
      resultInKind,
      markers
    ),
    usdValueDataPoints: _convertBalanceHistoryToDataPoints(resultUsd, markers),
    assetAmountMarkersDataPoints: _markers?.assetAmountMarkers || null,
    usdValueMarkersDataPoints: _markers?.usdValueMarkers || null,
    errored: false,
    loading: false,
  };
};
